import { Restart16, WarningFilled16 } from '@carbon/icons-react';
import { GetRowIdParams, SelectionChangedEvent } from 'ag-grid-community';
import { Col, Divider, Row, Tooltip } from 'antd';
import moment from 'moment';
import { pipe } from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { AiFillAppDestination, AiFillRuntimeInput } from 'api/ai-fill/types';
import { Meeting } from 'api/meeting/types';
import { CalendarIntegration } from 'api/timesheets/integration/calendar-integration-provider';
import { Button } from 'components/atoms/button';
import { CustomLayoutDrawerContent } from 'components/atoms/drawer';
import { withExternalFilter } from 'components/atoms/grid';
import { Grid as GridAtom } from 'components/atoms/grid/grid';
import { IconWrapper } from 'components/atoms/icon-shell';
import { AIFill16 } from 'components/atoms/icons/ai-fill';
import { SearchInput, TextArea } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { Tag } from 'components/atoms/tag';
import { FilterBar } from 'components/molecules/filter-bar';
import { Profile } from 'components/organisms/timesheets/drawers/calendar-integration/profile';
import { HelperText01 } from 'components/styles';
import { useCurrentProject } from 'hooks/use-current-project';
import { useTranslate } from 'hooks/use-translation';
import { useGetAllMeetings, useGetMeetingSettings } from 'queries/meetings';
import { sortByText } from 'utils/sort-by-text';
import { MeetingCompanyFilterType } from 'vendor/ckeditor/react-components/ai-fill/meetings-selector-v2/meeting-selector-content';
import {
  MeetingsEmptyState,
  MeetingsNoSearchResultState,
  ParticipantsRenderer,
} from 'vendor/ckeditor/react-components/ai-fill/meetings-selector/renderers';
import {
  getFunctionForFilterByCompany,
  getDateForMeetingsCalendar,
  filterMeetings,
} from 'vendor/ckeditor/react-components/ai-fill/utils';

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
const DrawerContent = styled(CustomLayoutDrawerContent)`
  ${CustomLayoutDrawerContent.Content} {
    padding: 0 !important;
  }
  ${CustomLayoutDrawerContent.Footer} {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-05);
  }
`;
const GridContainer = styled.div`
  padding-left: var(--spacing-05);
  padding-right: var(--spacing-05);
  width: 100%;
  height: 70%;
  position: relative;
`;

const Grid = styled(GridAtom)`
  .ag-react-container {
    > :not(:first-child) {
      display: none !important;
    }
    width: 100%;
    > div {
      width: 100%;
    }
  }
  .ag-row {
    border-bottom: 1px solid var(--border-color);
    .ag-selection-checkbox {
      opacity: 1;
    }
  }
  .ag-row-odd {
    background-color: var(--ag-background-color, var(--white-100));
  }
  .ag-row-focus {
    background-color: var(--ag-background-color, var(--white-100));
  }
  .ag-row-hover {
    background: var(--ag-row-hover-color);
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
    align-items: flex-start;
    .ag-react-container {
      padding-left: var(--layout-06);
      padding-top: var(--layout-06);
    }
  }
  .ag-cell {
    padding: 0 var(--cell-padding);
  }
`;
const PromptWrapper = styled.div`
  padding-left: var(--spacing-05);
  padding-right: var(--spacing-05);
  margin-bottom: var(--spacing-06);
`;
const PromptHelper = styled.div`
  ${HelperText01};
  margin-bottom: var(--spacing-04);
  color: var(--gray-70);
`;
const NoMeetingError = styled.div`
  background-color: #fffbe5;
  padding: var(--spacing-05);

  padding-bottom: var(--spacing-06);
`;
const HeadingRow = styled(Row)`
  padding-bottom: var(--spacing-02);
  font-weight: var(--font-weight-bold);
`;
const PrimaryButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-05);
`;
const StyledDrawerFooter = styled(CustomLayoutDrawerContent.Footer)`
  display: flex;
  justify-content: space-between !important;
`;

const TagWrapper = styled.div`
  display: flex;
`;

const ProfileIconWrapper = styled.span`
  height: 2rem;
  min-width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledSecondaryFilterBar = styled(FilterBar.Secondary)`
  display: flex;
  gap: var(--spacing-03);
`;

const StyledCustomLayoutDrawerContent = styled(
  CustomLayoutDrawerContent.Content,
)``;

const StyledFilterBar = styled(FilterBar)`
  padding-left: var(--spacing-03);
  padding-right: var(--spacing-04);
`;

const StyledGenerateButton = styled(Button)<{ disabled?: boolean }>`
  ${({ disabled }) =>
    !disabled &&
    css`
      background: linear-gradient(90deg, #0f62fe 0%, #099874 97.98%);
    `};
  svg path {
    fill: ${({ disabled }) =>
      disabled ? 'var(--gray-40)' : 'var(--white-100)'};
  }
`;

const FilterableGrid = pipe(pipe(() => Grid, withExternalFilter))();

const onModelUpdated = ({ api }) => {
  if (api.getDisplayedRowCount() === 0) {
    api.showNoRowsOverlay();
  } else {
    api.hideOverlay();
  }
};

interface Props {
  onClose: VoidFunction;
  onBack: VoidFunction;
  onFinish: (aiFillConfig?: AiFillRuntimeInput) => void;
  calendarIntegration?: CalendarIntegration;
}

export const SelectMeetings = ({
  onClose,
  onBack,
  onFinish,
  calendarIntegration,
}: Props) => {
  const translate = useTranslate();
  const { data: aiFillSettings, isLoading: aiFillSettingsLoading } =
    useGetMeetingSettings();
  const {
    data: meetings,
    isFetching: isMeetingsLoading,
    refetch: refetchMeetings,
  } = useGetAllMeetings();

  const [isCompanyFilterSelected, setIsSelectedCompanyFilter] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<Meeting | null>(null);

  const currentCustomerCompany = useCurrentProject().customer;

  const [showNoMeetingError, setShowNoMeetingError] = useState(false);
  const columns = useMemo(
    () => [
      {
        flex: 1,
        field: 'meetingName',
        headerName: translate(
          'ai_fill.meetings.select.grid.column.meeting_name',
        ),
        sortable: true,
        comparator: sortByText,
      },
      {
        width: 120,
        field: 'date',
        headerName: translate('ai_fill.meetings.select.grid.column.date'),
        sortable: true,
        valueFormatter: ({ data }) =>
          getDateForMeetingsCalendar(data?.startTime),
        comparator: sortByText,
      },
      {
        width: 120,
        field: 'startTime',
        headerName: translate('ai_fill.meetings.select.grid.column.start_time'),
        sortable: true,
        valueFormatter: ({ value }) => moment(value).format('HH:mm A'),
        comparator: sortByText,
      },
      {
        width: 120,
        field: 'participants',
        headerName: translate(
          'ai_fill.meetings.select.grid.column.participants',
        ),
        sortable: false,
        cellRenderer: 'ParticipantsRenderer',
        valueGetter: ({ data }) => data?.meta?.participants,
      },
    ],
    [translate],
  );
  const [searchText, setSearchText] = useState('');
  const [additionalContextText, setAdditionalContextText] = useState('');
  const filterFunction = useCallback(
    (meeting) => {
      if (isCompanyFilterSelected) {
        if (
          !getFunctionForFilterByCompany(
            MeetingCompanyFilterType.COMPANY,
            currentCustomerCompany.companyId,
          )(meeting)
        ) {
          return false;
        }
      }

      return (
        meeting?.meetingName
          ?.toLowerCase()
          .includes(searchText.toLowerCase().trim()) ?? false
      );
    },
    [searchText, isCompanyFilterSelected, currentCustomerCompany.companyId],
  );
  const isThereAnyCompanyMeetings = useMemo(
    () =>
      (meetings
        ? filterMeetings(
            meetings,
            MeetingCompanyFilterType.COMPANY,
            '',
            currentCustomerCompany.companyId,
          )
        : []
      ).length !== 0,
    [meetings, currentCustomerCompany.companyId],
  );
  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedData = api.getSelectedNodes();

      if (selectedData[0] && selectedData[0].id !== null) {
        setSelected(selectedData[0].data);
        if (!(selectedData[0].data as Meeting).summaryAvailable) {
          setShowNoMeetingError(true);
        } else {
          setShowNoMeetingError(false);
        }
      }

      api.redrawRows();
    },
    [setSelected],
  );

  return (
    <DrawerContent>
      <CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Title.Description>
          <FormattedMessage id="ai_fill.meetings.select.drawer.description" />
        </CustomLayoutDrawerContent.Title.Description>
        <FormattedMessage id="ai_fill.meetings.select.drawer.connected.title" />
      </CustomLayoutDrawerContent.Title>
      {isMeetingsLoading || aiFillSettingsLoading ? (
        <CustomLayoutDrawerContent.Content>
          <LoadingWrapper>
            <Loading size="large" />
          </LoadingWrapper>
        </CustomLayoutDrawerContent.Content>
      ) : (meetings ?? []).length === 0 ? (
        <CustomLayoutDrawerContent.Content>
          <MeetingsEmptyState />
        </CustomLayoutDrawerContent.Content>
      ) : (
        <StyledCustomLayoutDrawerContent>
          <StyledFilterBar>
            <FilterBar.Primary>
              <>
                {isThereAnyCompanyMeetings ? (
                  <TagWrapper>
                    <Tag
                      color={isCompanyFilterSelected ? 'blue' : 'gray'}
                      onClick={() => setIsSelectedCompanyFilter(true)}
                    >
                      <FormattedMessage
                        id="status_updates.meeting_tempalates.filter.company_meetings"
                        values={{
                          companyName: currentCustomerCompany.companyName,
                        }}
                      />
                    </Tag>
                    <Tag
                      color={!isCompanyFilterSelected ? 'blue' : 'gray'}
                      onClick={() => setIsSelectedCompanyFilter(false)}
                    >
                      <FormattedMessage id="status_updates.meeting_tempalates.filter.all_meetings" />
                    </Tag>
                  </TagWrapper>
                ) : (
                  <></>
                )}
              </>
            </FilterBar.Primary>
            <StyledSecondaryFilterBar>
              <SearchInput
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="small"
                direction="right"
                searchButtonProps={{ type: 'text' }}
              />
              <Tooltip
                title={translate(
                  'status_updates.meeting_tempalates.refresh_meetings.tooltip',
                )}
              >
                <Button
                  type="text"
                  size="small"
                  onClick={() => refetchMeetings()}
                  icon={
                    <IconWrapper>
                      <Restart16 />
                    </IconWrapper>
                  }
                />
              </Tooltip>
              {!aiFillSettings?.gongEnabled && calendarIntegration ? (
                <ProfileIconWrapper>
                  <Profile integration={calendarIntegration} size={20} />
                </ProfileIconWrapper>
              ) : null}
            </StyledSecondaryFilterBar>
          </StyledFilterBar>
          <GridContainer className="ag-theme-balham">
            <FilterableGrid
              suppressContextMenu
              suppressCellFocus
              rowSelection="single"
              getRowId={({ data }: GetRowIdParams) =>
                `${data.meetingId.toString()}-${moment(data.startTime)
                  .unix()
                  .toString()}`
              }
              defaultColDef={{
                filter: false,
                resizable: false,
                suppressMovable: true,
                suppressMenu: true,
                editable: false,
                unSortIcon: true,
                wrapText: true,
              }}
              components={{
                ParticipantsRenderer,
                MeetingsNoSearchResultState,
              }}
              filter={filterFunction}
              size="medium"
              rowData={meetings}
              columnDefs={columns}
              onSelectionChanged={onSelectionChanged}
              noRowsOverlayComponent="MeetingsNoSearchResultState"
              onModelUpdated={onModelUpdated}
            />
          </GridContainer>
          {selected === null ? (
            <></>
          ) : showNoMeetingError ? (
            <PromptWrapper>
              <Divider />
              <NoMeetingError>
                <Row gutter={16}>
                  <Col>
                    <WarningFilled16 fill="#F0C11B" color="#000" />
                  </Col>
                  <Col>
                    <HeadingRow>
                      <FormattedMessage id="status_updates.meeting_tempalates.no_meeting_found.title" />
                    </HeadingRow>
                    <Row>
                      <FormattedMessage id="status_updates.meeting_tempalates.no_meeting_found.description" />
                    </Row>
                  </Col>
                </Row>
              </NoMeetingError>
            </PromptWrapper>
          ) : (
            <PromptWrapper>
              <Divider />
              <PromptHelper>
                <FormattedMessage id="status_updates.meeting_templates.prompt.label" />
              </PromptHelper>
              <TextArea
                autoSize={{ minRows: 2, maxRows: 3 }}
                onChange={(e) => setAdditionalContextText(e.target.value)}
                onPressEnter={(e) =>
                  setAdditionalContextText(e.currentTarget.value)
                }
              />
            </PromptWrapper>
          )}
        </StyledCustomLayoutDrawerContent>
      )}
      <StyledDrawerFooter>
        <Button onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <PrimaryButtonGroup>
          <Button type="tertiary" onClick={onBack}>
            <FormattedMessage id="back" />
          </Button>
          {showNoMeetingError && selected ? (
            <Button
              type="primary"
              onClick={() => {
                onFinish();
                setAdditionalContextText('');
              }}
            >
              <FormattedMessage id="next" />
            </Button>
          ) : (
            <StyledGenerateButton
              type="primary"
              icon={<AIFill16 />}
              iconPlacement="left"
              disabled={!selected}
              onClick={() => {
                onFinish({
                  sources: [
                    { sourceId: selected?.meetingId, sourceType: 'MEETING' },
                  ],
                  destination: AiFillAppDestination.ProjectUpdate,
                  writeAsEmail: true,
                  additionalContext: additionalContextText,
                } as AiFillRuntimeInput);
                setAdditionalContextText('');
              }}
            >
              <FormattedMessage id="generate" />
            </StyledGenerateButton>
          )}
        </PrimaryButtonGroup>
      </StyledDrawerFooter>
    </DrawerContent>
  );
};
